import * as React from "react";
import { cx } from "../internal/cx.js";

export type InfoFieldRowLayout = "stacked" | "inline";
export type InfoFieldRowAlign = "start" | "end";
export type InfoFieldRowSize = "md" | "sm";

export interface InfoFieldRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Optional leading icon. Decorative by default; include accessible text in label/value. */
  icon?: React.ReactNode;
  /** Label text (usually a short noun phrase like "Date of birth"). */
  label: React.ReactNode;
  /** The displayed value. Falls back to the empty-state when nullish. */
  value?: React.ReactNode;
  /** Layout direction. Default 'stacked'. */
  layout?: InfoFieldRowLayout;
  /** Horizontal alignment of the value column. Only applies when layout='inline'. */
  align?: InfoFieldRowAlign;
  /** What to render when value is null/undefined/''. Default an em-dash. */
  emptyState?: React.ReactNode;
  /** Optional trailing slot (e.g. copy button, edit link). */
  action?: React.ReactNode;
  /** Optional second-line context under the value. */
  helperText?: React.ReactNode;
  /** Visual density. Default 'md'. */
  size?: InfoFieldRowSize;
}

function isEmpty(value: React.ReactNode): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  return false;
}

/**
 * Label + value pair for read-only detail panes (profile sheets, summary
 * cards, confirmation steps). Intentionally dumb — no truncation, no
 * copy-to-clipboard, no affordances. Compose those at the page level.
 */
export const InfoFieldRow = React.forwardRef<HTMLDivElement, InfoFieldRowProps>(
  function InfoFieldRow(
    {
      action,
      align = "start",
      className,
      emptyState = "—",
      helperText,
      icon,
      label,
      layout = "stacked",
      size = "md",
      value,
      ...props
    },
    ref,
  ) {
    const isValueEmpty = isEmpty(value);
    return (
      <div
        ref={ref}
        className={cx(
          "cw-v2-info-field-row",
          `cw-v2-info-field-row--${layout}`,
          `cw-v2-info-field-row--${size}`,
          `cw-v2-info-field-row--align-${align}`,
          icon ? "cw-v2-info-field-row--has-icon" : null,
          className,
        )}
        {...props}
      >
        {icon ? (
          <div aria-hidden className="cw-v2-info-field-row__icon">
            {icon}
          </div>
        ) : null}
        <div className="cw-v2-info-field-row__label">{label}</div>
        <div className="cw-v2-info-field-row__value-wrap">
          <div
            className={cx(
              "cw-v2-info-field-row__value",
              isValueEmpty && "cw-v2-info-field-row__value--empty",
            )}
          >
            {isValueEmpty ? emptyState : value}
          </div>
          {helperText ? <div className="cw-v2-info-field-row__helper">{helperText}</div> : null}
        </div>
        {action ? <div className="cw-v2-info-field-row__action">{action}</div> : null}
      </div>
    );
  },
);
InfoFieldRow.displayName = "InfoFieldRow";
